import {Agent} from '../agent';
import {ArchivedFna} from '../archived-fna';
import {
  ArchivedPersonInsurance,
  ArchivedProposal,
  ArchivedProposalAgent,
  ArchivedProtection,
  ArchivedRider,
  ArchivedUserInfo,
  HealthAnswersRevision,
} from '../archived-proposal';
import {Client} from '../client/client';
import {
  Duration,
  HUMANIA_EMMA_CHILD_RIDER,
  HUMANIA_EMMA_CHILD_RIDER_COVERAGES_AND_ANNUAL_PRICES,
  HUMANIA_EMMA_CHILD_RIDER_COVERAGES_AND_PRICES,
  InsuranceFirm,
  insuranceFirmAgentInfo,
  PriceBreakdown,
  ProviderId,
  RiderType,
} from '../insurance';
import {BACKEND_INSURER_TO_INSURER, Insurer} from '../insurer';
import {ProposalExtra} from '../proposal';
import {TextTranslations} from '../question';
import {UnderwritingProfile} from '../underwriting';
import {EMMA_DEFAULT_EMAIL} from '../url/emma';
import {SaleStatus} from './sale-status';

export type MinifiedSaleReport = Pick<
  SaleReport,
  'id' | 'policyNumber' | 'userId' | 'status' | 'modificationNotice' | 'modificationDecisionMadeAt'
> & {
  proposal: Pick<ArchivedProposal, 'id'> & {
    submitter: Pick<ArchivedUserInfo, 'id' | 'firstName' | 'lastName' | 'phone'>;
    healthAnswersRevisions?: Pick<HealthAnswersRevision, 'id' | 'completedAt'>[];
    extra: Pick<ProposalExtra, 'idDocument'>;
    agents: Pick<ArchivedProposalAgent, 'firstName' | 'lastName' | 'isPrimary'>[];
    underwritingProfile?: Pick<UnderwritingProfile, 'status'> & {
      extraRequirementsFile?: {
        id: string;
        extraRequirementsInquiries: {id: string}[];
        hasDocument: boolean;
      };
    };
  };
  protection: {
    personInsurances: Pick<ArchivedPersonInsurance, 'price' | 'coverage' | 'duration'>[] & {
      riders?: Pick<ArchivedRider, 'type'>[];
    };
  };
};

export interface SaleReport {
  id: string;
  userId?: string;
  policyNumber?: string;
  insurer: Insurer;
  protection: ArchivedProtection & {surchargeRate: number};
  fna: ArchivedFna;
  proposal: ArchivedProposal;
  type: SaleReportType;
  status: SaleReportStatus;
  createdAt: Date;
  contractReceived: boolean;
  approvedByInsurerAt?: Date;
  inForceAt?: Date;
  refusalNotice?: TextTranslations;
  modificationNotice?: TextTranslations;
  modificationConditionText?: string;
  modificationNoticeSentAt?: Date;
  modificationDecisionMadeAt?: Date;
  modificationDecision?: ModificationDecision;
  secretCode?: string;
}

export interface BackendSaleReport {
  id: string;
  clientId: string;
  client: Client;
  status: SaleStatus;
  revisionHash: string;
  policyNumber: string;
  policyType: string;
  faceValue: number;
  annualPremium: number;
  completed: boolean;
  illustrationFileName: string;
  completionAt: Date;
  inforceAt: Date;
  commissionAdvisor: number;
  commissionReferral: number;
  commissionTotal: number;
  nbPolicyHolder: number;
  policyHolderName: string[];
  childEndorsement: '25000' | '15000';
  replacementNoticeFileName: string;
  replacementNoticeCheck: boolean;
  emmaLifeInsurance: boolean;
  purchaseDate: Date;
  effectiveDate: Date;
  prelevementDate: string;
  insurer: {id: string; name: string};
  advisor: Agent;
}

export enum InsuranceType {
  LIFE = 'life',
  DISABILITY = 'disability',
  CRITICAL_ILLNESS = 'critical-illness',
}

export enum SaleReportType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

export enum SaleReportStatus {
  ONGOING = 'ONGOING',
  ILLUSTRATION = 'ILLUSTRATION',
  UNDERWRITING = 'UNDERWRITING',
  IN_FORCE = 'IN_FORCE',
  REFUSED_BY_INSURER = 'REFUSED_BY_INSURER',
  APPROVED_BY_INSURER = 'APPROVED_BY_INSURER',
  REFUSED_BY_CLIENT = 'REFUSED_BY_CLIENT',
  LAPSED = 'LAPSED',
  UNPAID = 'UNPAID',
  UNKNOWN = 'UNKNOWN',
  ABANDONED = 'ABANDONED',
}

export type Decision = 'ACCEPTED' | 'REFUSED';

export enum ModificationDecision {
  ACCEPTED = 'ACCEPTED',
  REFUSED = 'REFUSED',
}

export function backendSaleReportToSaleReport(backendSaleReport: BackendSaleReport): SaleReport {
  return {
    id: atob(backendSaleReport.id),
    userId: atob(backendSaleReport.clientId),
    policyNumber: backendSaleReport.policyNumber,
    insurer: BACKEND_INSURER_TO_INSURER.get(backendSaleReport.insurer?.name) ?? Insurer.UNKNOWN,
    protection: {
      personInsurances: [
        {
          type: InsuranceType.LIFE,
          duration: <Duration>backendSaleReport.policyType.substring(22).replace(')', ''),
          coverage: backendSaleReport.faceValue,
          price: backendSaleReport.annualPremium * 0.09,
          annualPrice: backendSaleReport.annualPremium,
          riders:
            backendSaleReport.childEndorsement === HUMANIA_EMMA_CHILD_RIDER.OPTION_25K.toString() ||
            backendSaleReport.childEndorsement === HUMANIA_EMMA_CHILD_RIDER.OPTION_15K.toString()
              ? [
                  {
                    type: RiderType.CHILD,
                    coverage: +backendSaleReport.childEndorsement,
                    price: HUMANIA_EMMA_CHILD_RIDER_COVERAGES_AND_PRICES.get(
                      +backendSaleReport.childEndorsement,
                    )!,
                    annualPrice: HUMANIA_EMMA_CHILD_RIDER_COVERAGES_AND_ANNUAL_PRICES.get(
                      +backendSaleReport.childEndorsement,
                    )!,
                  },
                ]
              : [],
          priceBreakdown: {} as PriceBreakdown,
        },
      ],
      surchargeRate: 0,
    },
    fna: {} as any, // All backend sale reports are external and their FNAs are not supported
    proposal: {
      id: atob(backendSaleReport.id),
      providerId: ProviderId.HUMANIA_EMMA,
      payer: oldClientToUserInfo(backendSaleReport),
      submitter: oldClientToUserInfo(backendSaleReport),
      insuree: oldClientToUserInfo(backendSaleReport),
      agents: [
        {
          agentId: backendSaleReport.advisor.id,
          commission: backendSaleReport.commissionAdvisor,
          email: backendSaleReport.advisor.email,
          firstName: backendSaleReport.advisor.firstName,
          lastName: backendSaleReport.advisor.lastName,
          isServicing: false,
          isPrimary: true,
        },
        {
          agentId: InsuranceFirm.EMMA,
          commission: 100 - backendSaleReport.commissionAdvisor,
          isServicing: true,
          isPrimary: false,
          firstName: insuranceFirmAgentInfo.EMMA.firstName,
          lastName: insuranceFirmAgentInfo.EMMA.lastName,
          email: EMMA_DEFAULT_EMAIL,
        },
      ],
    } as ArchivedProposal,
    type: SaleReportType.INTERNAL,
    status: saleStatusToSaleReportStatus(backendSaleReport.status),
    createdAt: backendSaleReport.purchaseDate,
    contractReceived: !!backendSaleReport.policyNumber,
  };
}

function saleStatusToSaleReportStatus(status: SaleStatus): SaleReportStatus {
  switch (status) {
    case SaleStatus.INFORCE:
      return SaleReportStatus.IN_FORCE;
    case SaleStatus.REFUSEDBYINSURER:
      return SaleReportStatus.REFUSED_BY_INSURER;
    case SaleStatus.REFUSEDBYCLIENT:
      return SaleReportStatus.REFUSED_BY_CLIENT;
    default:
      return SaleReportStatus[status];
  }
}

function oldClientToUserInfo(backendSaleReport: BackendSaleReport): ArchivedUserInfo {
  return {
    firstName: backendSaleReport.client.firstName,
    lastName: backendSaleReport.client.lastName,
    languageCode: backendSaleReport.client.preferredLanguage === 'French' ? 'fr' : 'en',
    email: backendSaleReport.client.email,
    phone: backendSaleReport.client.cell,
    address: backendSaleReport.client.street,
    city: backendSaleReport.client.city,
    province: backendSaleReport.client.province,
  } as ArchivedUserInfo;
}
